import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    const note = event.target.value;
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('note-update:user-action', event);
    }
  }, 200);

  /**
   * Updates the cart drawer's "0/250" counter on every keystroke, then saves the note.
   * @param {InputEvent} event - The input event from the gift message textarea.
   */
  onGiftMessageInput(event) {
    const textarea = event.target;
    const count = this.refs.giftMessageCount;
    if (textarea instanceof HTMLTextAreaElement && count instanceof HTMLElement) {
      count.textContent = `${textarea.value.length}/${textarea.maxLength}`;
    }
    this.updateCartNote(event);
  }

  /**
   * Shows or hides the gift message field when the cart drawer's gift checkbox changes.
   * @param {Event} event - The change event from the checkbox.
   */
  toggleGiftMessage(event) {
    const message = this.refs.giftMessage;
    if (!(message instanceof HTMLElement)) return;
    const checked = event.target instanceof HTMLInputElement && event.target.checked;
    message.hidden = !checked;
    if (checked) message.querySelector('textarea')?.focus();
  }
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
